// Gemeinsame Auswertung der Besichtigung (Kartons & Kubik je Raum, Umzug vs. Entsorgung).
// Wird von Sidebar, Angebot und Pipedrive-Übertragung genutzt, damit überall dieselben Zahlen stehen.

export const DISPOSAL_ROOM = 'Entsorgung';

export const CARTON_TYPES = [
  { name: 'Umzugskartons (Standard)', label: 'Umzugskartons' },
  { name: 'Bücherkartons (Bücher&Geschirr)', label: 'Bücher-/Porzellankartons' },
  { name: 'Kleiderkisten', label: 'Kleiderkisten' }
];

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * additionalInfo kommt je nach Quelle als Feld-Array, als Kategorien-Array (mit fields)
 * oder – nach Auto-Save/Restore – als Objekt { 0: ..., 1: ... }. Liefert immer ein flaches Feld-Array.
 */
export const normalizeAdditionalInfo = (additionalInfo) => {
  if (!additionalInfo) return [];
  const list = Array.isArray(additionalInfo) ? additionalInfo : Object.values(additionalInfo);
  return list.flatMap(entry => (entry && Array.isArray(entry.fields) ? entry.fields : [entry])).filter(Boolean);
};

/** Manuell bestätigte Kartonmengen aus "Zusätzliche Details" (0 wenn nicht gesetzt). */
export const getManualCartonCounts = (additionalInfo) => {
  const fields = normalizeAdditionalInfo(additionalInfo);
  const valueOf = (name) => toNumber(fields.find(f => f.name === name)?.value);
  return {
    umzugskartons: valueOf('Anzahl Umzugskartons'),
    porzellankartons: valueOf('Anzahl Porzellankartons')
  };
};

const roomItemsVolume = (room) => (room.items || []).reduce(
  (sum, item) => sum + (toNumber(item.length) * toNumber(item.width) * toNumber(item.height) * toNumber(item.quantity)) / 1000000,
  0
);

/** Kubik und Kartons je Raum. Entsorgung wird markiert, damit sie getrennt summiert werden kann. */
export const getRoomBreakdown = (rooms) => Object.entries(rooms || {})
  .map(([roomName, room]) => {
    const materials = room?.packMaterials || [];
    const cartons = CARTON_TYPES.reduce((acc, { name }) => {
      acc[name] = materials
        .filter(m => m.name === name)
        .reduce((sum, m) => sum + toNumber(m.quantity), 0);
      return acc;
    }, {});
    const cartonTotal = Object.values(cartons).reduce((a, b) => a + b, 0);
    const volume = room?.totalVolume != null ? toNumber(room.totalVolume) : roomItemsVolume(room || {});
    return { roomName, volume, cartons, cartonTotal, isDisposal: roomName === DISPOSAL_ROOM };
  })
  .filter(r => r.volume > 0 || r.cartonTotal > 0);

export const formatCartons = (cartons) => CARTON_TYPES
  .filter(({ name }) => cartons[name] > 0)
  .map(({ name, label }) => `${cartons[name]}x ${label}`)
  .join(', ');

export const MONTAGE_OPTIONS = [
  { key: 'demontiert', label: 'Demontage' },
  { key: 'remontiert', label: 'Remontage' },
  { key: 'duebelarbeiten', label: 'Dübelarbeiten' },
  { key: 'elektro', label: 'Elektro' }
];

/** Alle Gegenstände mit Montageleistungen; gleiche Gegenstände mit gleichen Leistungen werden raumübergreifend summiert. */
export const getMontageList = (rooms) => {
  const entries = new Map();
  Object.values(rooms || {}).forEach(room => {
    (room?.items || []).forEach(item => {
      const quantity = toNumber(item.quantity);
      const tasks = MONTAGE_OPTIONS.filter(({ key }) => item[key]).map(({ label }) => label);
      if (quantity <= 0 || tasks.length === 0) return;
      const key = `${item.name}|${tasks.join(',')}`;
      const entry = entries.get(key) || { name: item.name, tasks, quantity: 0 };
      entry.quantity += quantity;
      entries.set(key, entry);
    });
  });
  return [...entries.values()];
};

/** Montagen als Textliste zum Einfügen ins Angebot. */
export const formatMontageListText = (montageList) => montageList
  .map(({ name, quantity, tasks }) => `• ${quantity}x ${name}: ${tasks.join(', ')}`)
  .join('\n');

/** "Möbel die bearbeitet werden müssen" – identisch in Personal-Anmerkungen und Pipedrive-Notiz. */
export const formatItemsToProcessText = (items) => Object.entries(items || {})
  .filter(([_, item]) => item.duebelarbeiten || item.demontiert)
  .map(([name, item]) =>
    `${name}: ${item.quantity} (${[
      item.demontiert ? 'Demontiert' : '',
      item.duebelarbeiten ? 'Dübelarbeiten' : ''
    ].filter(Boolean).join(', ')})`
  )
  .join('\n');

/** Textblock "Kubik & Kartons je Raum" für Pipedrive-Notiz und Personal-Anmerkungen. */
export const formatRoomBreakdownText = (breakdown, indent = '  ') => breakdown
  .map(({ roomName, volume, cartons, cartonTotal }) => {
    const cartonText = cartonTotal > 0 ? ` | Kartons: ${cartonTotal} (${formatCartons(cartons)})` : '';
    return `${indent}• ${roomName}: ${volume.toFixed(2)} m³${cartonText}`;
  })
  .join('\n');
